import { useState, useEffect, useCallback } from 'react';
import { Listing } from '@/types/listing';

// ─── Storage keys ─────────────────────────────────────────────────────────────
const META_KEY = 'florida_homes_listings_v3'; // listing metadata (no images)
const IDB_DB   = 'florida_homes_photos';
const IDB_STORE = 'photos'; // key = listing id, value = string[]

// ─── IndexedDB helpers ────────────────────────────────────────────────────────
function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

async function idbGetPhotos(id: string): Promise<string[]> {
  try {
    const db = await openIDB();
    return await new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(id);
      req.onsuccess = () => resolve(Array.isArray(req.result) ? req.result : []);
      req.onerror   = () => resolve([]);
    });
  } catch {
    return [];
  }
}

async function idbSetPhotos(id: string, photos: string[]): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve, reject) => {
      const tx  = db.transaction(IDB_STORE, 'readwrite');
      const req = tx.objectStore(IDB_STORE).put(photos, id);
      req.onsuccess = () => resolve();
      req.onerror   = () => reject(req.error);
    });
  } catch {
    // ignore
  }
}

async function idbDeletePhotos(id: string): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(id);
      tx.oncomplete = () => resolve();
    });
  } catch {
    // ignore
  }
}

// ─── Metadata (no images) stored in localStorage ──────────────────────────────
type ListingMeta = Omit<Listing, 'images'>;

function readMeta(): ListingMeta[] {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ListingMeta[];
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // ignore
  }
  return [];
}

function writeMeta(listings: ListingMeta[]): void {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(listings));
  } catch {
    // ignore
  }
}

// ─── Hydrate: merge meta + photos from IDB ────────────────────────────────────
async function hydrateListings(meta: ListingMeta[]): Promise<Listing[]> {
  const results = await Promise.all(
    meta.map(async (m) => {
      const images = await idbGetPhotos(m.id);
      return { ...m, images };
    })
  );
  return results;
}

// ─── Singleton reactive store ─────────────────────────────────────────────────
type Listener = (listings: Listing[]) => void;
const listeners = new Set<Listener>();
let globalListings: Listing[] = [];
let hydrated = false;

function broadcast(next: Listing[]): void {
  globalListings = next;
  // Persist meta (no images) to localStorage
  writeMeta(next.map(({ images: _images, ...meta }) => meta));
  listeners.forEach((fn) => fn([...next]));
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useListings() {
  const [listings, setListings] = useState<Listing[]>(() => [...globalListings]);

  useEffect(() => {
    listeners.add(setListings);

    // Hydrate once on first mount
    if (!hydrated) {
      hydrated = true;
      const meta = readMeta();
      hydrateListings(meta).then((full) => {
        globalListings = full;
        listeners.forEach((fn) => fn([...full]));
      });
    } else {
      setListings([...globalListings]);
    }

    const handleStorage = (e: StorageEvent) => {
      if (e.key === META_KEY) {
        const meta = readMeta();
        hydrateListings(meta).then((full) => {
          globalListings = full;
          listeners.forEach((fn) => fn([...full]));
        });
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      listeners.delete(setListings);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const addListing = useCallback(async (listing: Omit<Listing, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    // Save photos to IDB first
    await idbSetPhotos(id, listing.images ?? []);
    const newListing: Listing = { ...listing, id };
    broadcast([newListing, ...globalListings]);
  }, []);

  const updateListing = useCallback(async (id: string, updated: Omit<Listing, 'id'>) => {
    await idbSetPhotos(id, updated.images ?? []);
    broadcast(globalListings.map((l) => (l.id === id ? { ...updated, id } : l)));
  }, []);

  const deleteListing = useCallback(async (id: string) => {
    await idbDeletePhotos(id);
    broadcast(globalListings.filter((l) => l.id !== id));
  }, []);

  return { listings, addListing, updateListing, deleteListing };
}
